// The operations that change prose (plan §6.1), in the wire form the server
// accepts, applied to a plain picture of the document. Every operation has a
// computable inverse, which is what gives the document an undo stack.
//
// Positions are "after this id", never an index, so an operation means the
// same thing when it arrives after someone else's edit.

export type ParagraphId = string;

export type Op =
  | { t: "set_text"; id: ParagraphId; text: string }
  | { t: "insert"; id: ParagraphId; after: ParagraphId | null; text: string }
  | { t: "delete"; id: ParagraphId }
  | { t: "move"; id: ParagraphId; after: ParagraphId | null }
  | { t: "split"; id: ParagraphId; new_id: ParagraphId; at: number }
  | { t: "merge"; survivor: ParagraphId; absorbed: ParagraphId }
  | { t: "set_title"; title: string };

export interface DocState {
  title: string;
  order: ParagraphId[];
  text: Record<ParagraphId, string>;
}

export class OpError extends Error {}

export function newId(): ParagraphId {
  return crypto.randomUUID();
}

export function emptyDoc(): DocState {
  const id = newId();
  return { title: "", order: [id], text: { [id]: "" } };
}

export function docFromParagraphs(paragraphs: string[], title = ""): DocState {
  const texts = paragraphs.length > 0 ? paragraphs : [""];
  const order = texts.map(() => newId());
  const text: Record<ParagraphId, string> = {};
  order.forEach((id, i) => {
    text[id] = texts[i] ?? "";
  });
  return { title, order, text };
}

export function paragraphsOf(doc: DocState): string[] {
  return doc.order.map((id) => doc.text[id] ?? "");
}

function indexAfter(order: ParagraphId[], after: ParagraphId | null): number {
  if (after === null) return 0;
  const i = order.indexOf(after);
  if (i < 0) throw new OpError(`no such paragraph to follow: ${after}`);
  return i + 1;
}

/** Apply one operation and return the new document. Throws OpError when the
 *  operation does not fit, mirroring the server's checks. */
export function applyOp(doc: DocState, op: Op): DocState {
  switch (op.t) {
    case "set_title":
      return doc.title === op.title ? doc : { ...doc, title: op.title };

    case "set_text": {
      if (!(op.id in doc.text)) throw new OpError(`no such paragraph: ${op.id}`);
      if (doc.text[op.id] === op.text) return doc;
      return { ...doc, text: { ...doc.text, [op.id]: op.text } };
    }

    case "insert": {
      if (op.id in doc.text) throw new OpError(`paragraph already exists: ${op.id}`);
      const at = indexAfter(doc.order, op.after);
      const order = [...doc.order.slice(0, at), op.id, ...doc.order.slice(at)];
      return { ...doc, order, text: { ...doc.text, [op.id]: op.text } };
    }

    case "delete": {
      if (!(op.id in doc.text)) throw new OpError(`no such paragraph: ${op.id}`);
      const text = { ...doc.text };
      delete text[op.id];
      return { ...doc, order: doc.order.filter((x) => x !== op.id), text };
    }

    case "move": {
      if (!doc.order.includes(op.id)) throw new OpError(`no such paragraph: ${op.id}`);
      if (op.after === op.id) throw new OpError("cannot position a paragraph after itself");
      const rest = doc.order.filter((x) => x !== op.id);
      const at = indexAfter(rest, op.after);
      const order = [...rest.slice(0, at), op.id, ...rest.slice(at)];
      return order.every((x, k) => x === doc.order[k]) ? doc : { ...doc, order };
    }

    case "split": {
      const t = doc.text[op.id];
      if (t === undefined) throw new OpError(`no such paragraph: ${op.id}`);
      if (op.new_id in doc.text) throw new OpError(`paragraph already exists: ${op.new_id}`);
      if (op.at < 0 || op.at > t.length) throw new OpError(`split offset out of range: ${op.at}`);
      const i = doc.order.indexOf(op.id);
      const order = [...doc.order.slice(0, i + 1), op.new_id, ...doc.order.slice(i + 1)];
      return {
        ...doc,
        order,
        text: { ...doc.text, [op.id]: t.slice(0, op.at), [op.new_id]: t.slice(op.at) },
      };
    }

    case "merge": {
      if (op.survivor === op.absorbed) throw new OpError("cannot merge a paragraph into itself");
      const i = doc.order.indexOf(op.survivor);
      const j = doc.order.indexOf(op.absorbed);
      if (i < 0 || j < 0) throw new OpError("both paragraphs must exist to merge");
      if (j !== i + 1) throw new OpError("only adjacent paragraphs can be merged");
      const text = { ...doc.text, [op.survivor]: doc.text[op.survivor]! + doc.text[op.absorbed]! };
      delete text[op.absorbed];
      return { ...doc, order: doc.order.filter((x) => x !== op.absorbed), text };
    }
  }
}

/** The operation that undoes `op`, given the document as it was *before* `op`.
 *  Note that undoing a delete brings the text back under its old id, but any
 *  annotation the server orphaned when it was deleted stays orphaned. */
export function invert(before: DocState, op: Op): Op {
  switch (op.t) {
    case "set_title":
      return { t: "set_title", title: before.title };
    case "set_text":
      return { t: "set_text", id: op.id, text: before.text[op.id] ?? "" };
    case "insert":
      return { t: "delete", id: op.id };
    case "delete":
      return {
        t: "insert",
        id: op.id,
        after: predecessor(before.order, op.id),
        text: before.text[op.id] ?? "",
      };
    case "move":
      return { t: "move", id: op.id, after: predecessor(before.order, op.id) };
    case "split":
      return { t: "merge", survivor: op.id, absorbed: op.new_id };
    case "merge":
      return {
        t: "split",
        id: op.survivor,
        new_id: op.absorbed,
        at: (before.text[op.survivor] ?? "").length,
      };
  }
}

export function predecessor(order: ParagraphId[], id: ParagraphId): ParagraphId | null {
  const i = order.indexOf(id);
  return i <= 0 ? null : (order[i - 1] ?? null);
}

/** The `after` id that puts `id` at index `to` of the order with `id` removed. */
export function afterForIndex(order: ParagraphId[], id: ParagraphId, to: number): ParagraphId | null {
  const rest = order.filter((x) => x !== id);
  const at = Math.max(0, Math.min(to, rest.length));
  return at === 0 ? null : (rest[at - 1] ?? null);
}
